import React, { useState } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { useConfirm } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { formatDate, STREFA_FIRMY } from '../../utils/format';
import {
  dzienPrzerwyZaplanowanej, dzienWZakresie, stanRezygnacjiFirmy, zakresDniRezygnacji,
} from '../../utils/rezygnacja';

// Okno „Rezygnacja” w panelu super-admina (FAZA F, F.25): rezygnacja od dziś albo od wybranego dnia
// (najwyżej rok naprzód, dzień w przyszłości tylko dla firmy, która dziś działa), zmiana dnia
// rezygnacji zaplanowanej i odwołanie. Od dnia rezygnacji konto jest tylko do odczytu jak w przerwie,
// a po 30 dniach dane firmy mogą zostać usunięte. Rezygnacja zastępuje przerwę (trwającą albo
// zaplanowaną), więc przy firmie w przerwie okno i potwierdzenie mówią, że przerwa zniknie, a przy
// przerwie zaplanowanej dzień rezygnacji kończy się na dniu jej początku. Reguły pilnuje serwer
// (Login/backend/lib/rezygnacjaKonta.js), okno tylko je opisuje. Style: pages/SuperAdmin.css.

function csrfToken() {
  const parts = `; ${document.cookie}`.split('; csrf=');
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(data.error || ''), { dane: data });
  return data;
}

const opcja = { display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' };

export default function ResignManageModal({ org, onClose, onChanged }) {
  const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const stan = stanRezygnacjiFirmy(org);
  const zakres = zakresDniRezygnacji(org);
  const [tryb, setTryb] = useState('today');
  const [dzien, setDzien] = useState(stan === 'zaplanowana' ? '' : zakres.min);
  const [busy, setBusy] = useState(false);
  // resigned_at to znacznik czasu (północ w Warszawie), termin usunięcia to sam dzień.
  const odDnia = formatDate(org.resigned_at, { locale: currentLanguage, fallback: '', timeZone: STREFA_FIRMY });
  const usuniecieOd = formatDate(org.resignation_delete_from, { locale: currentLanguage, fallback: '' });
  const url = (koncowka) => `${API_BASE}/api/super-admin/organizations/${org.id}/${koncowka}`;
  const dzienPL = (ymd) => formatDate(ymd, { locale: currentLanguage, fallback: ymd });
  // Przerwa, którą rezygnacja odwoła albo zakończy (tylko firma w statusie przerwy).
  const przerwaOd = dzienPrzerwyZaplanowanej(org);
  const przerwaTrwa = org.status === 'paused' && !przerwaOd;
  const notkaPrzerwy = przerwaOd
    ? t('resignManage.pausePlanned', { od: dzienPL(przerwaOd) })
    : przerwaTrwa
      ? t('resignManage.pauseOngoing', { od: formatDate(org.paused_at, { locale: currentLanguage, fallback: '', timeZone: STREFA_FIRMY }) })
      : '';
  const zNotkaPrzerwy = (tekst) => (notkaPrzerwy ? `${tekst} ${notkaPrzerwy}` : tekst);

  const wykonaj = async (koncowka, cialo, komunikat) => {
    setBusy(true);
    try {
      try {
        await postJson(url(koncowka), cialo);
      } catch (e) {
        // Rezerwacje od dnia rezygnacji i faktury, które nie trafiły do KSeF: serwer podaje liczby
        // i czeka na potwierdzenie.
        const d = e.dane || {};
        if (d.code !== 'resignation_open_reservations') throw e;
        const rezerwacje = (d.ongoingReservations || 0) + (d.upcomingReservations || 0);
        const czesci = [];
        if (d.unsentKsefInvoices > 0) czesci.push(t('resignManage.unsentKsef', { liczba: d.unsentKsefInvoices }));
        czesci.push(rezerwacje > 0
          ? t('resignManage.confirmOpenReservations', { trwajace: d.ongoingReservations || 0, nadchodzace: d.upcomingReservations || 0 })
          : t('resignManage.confirmAnyway'));
        const ok = await confirm({ title: czesci.join(' '), tone: 'danger' });
        if (!ok) return;
        await postJson(url(koncowka), { ...cialo, confirmOpenReservations: true });
      }
      toast.success(komunikat);
      onChanged();
    } catch (e) {
      toast.error(e.message || t('resignManage.error'));
    } finally {
      setBusy(false);
    }
  };

  const wybranyDzien = tryb === 'today' ? zakres.min : dzien;
  const dzienPoprawny = tryb === 'today' || dzienWZakresie(dzien, zakres);

  const ustaw = async () => {
    const odDzis = tryb === 'today';
    const ok = await confirm({
      title: zNotkaPrzerwy(odDzis
        ? t('resignManage.confirmToday', { name: org.name })
        : t('resignManage.confirmDay', { name: org.name, od: dzienPL(wybranyDzien) })),
      tone: 'danger',
    });
    if (!ok) return;
    await wykonaj('resign', { start: odDzis ? 'today' : wybranyDzien }, t('resignManage.saved'));
  };

  const zmienDzien = async () => {
    const ok = await confirm({ title: t('resignManage.confirmDay', { name: org.name, od: dzienPL(dzien) }), tone: 'danger' });
    if (!ok) return;
    await wykonaj('resign', { start: dzien }, t('resignManage.saved'));
  };

  const odDzisZaplanowanej = async () => {
    const ok = await confirm({ title: t('resignManage.confirmToday', { name: org.name }), tone: 'danger' });
    if (!ok) return;
    await wykonaj('resign', { start: 'today' }, t('resignManage.saved'));
  };

  const odwolaj = async () => {
    const ok = await confirm({ title: t('resignManage.confirmCancel', { name: org.name }) });
    if (!ok) return;
    await wykonaj('cancel-resignation', {}, t('resignManage.cancelled'));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-cy="resign-manage-modal">
        <div className="modal-header">
          <h2>{t('resignManage.title', { name: org.name })}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('common.cancel')}>×</button>
        </div>

        <div className="org-form">
          <p><small>{t('resignManage.explain')}</small></p>
          {stan === 'brak' && notkaPrzerwy && <p data-cy="resign-pause-note">{notkaPrzerwy}</p>}

          {stan === 'brak' && (
            <div className="form-group" role="radiogroup" aria-label={t('resignManage.startLabel')}>
              <label>{t('resignManage.startLabel')}</label>
              <label style={opcja}>
                <input type="radio" name="resign-start" checked={tryb === 'today'} onChange={() => setTryb('today')} data-cy="resign-start-today" />
                {t('resignManage.startToday')}
              </label>
              <label style={{ ...opcja, cursor: zakres.tylkoDzis ? 'not-allowed' : 'pointer' }}>
                <input
                  type="radio"
                  name="resign-start"
                  checked={tryb === 'day'}
                  onChange={() => setTryb('day')}
                  disabled={zakres.tylkoDzis}
                  data-cy="resign-start-day"
                />
                {t('resignManage.startDay')}
              </label>
              {zakres.tylkoDzis ? (
                <small data-cy="resign-today-only">{t('resignManage.todayOnly')}</small>
              ) : tryb === 'day' && (
                <input
                  type="date"
                  aria-label={t('resignManage.dayLabel')}
                  min={zakres.min}
                  max={zakres.max}
                  value={dzien}
                  onChange={(e) => setDzien(e.target.value)}
                  data-cy="resign-day"
                />
              )}
            </div>
          )}

          {stan === 'zaplanowana' && (
            <>
              <p data-cy="resign-manage-state">{t('resignManage.stateScheduled', { od: odDnia })}</p>
              <div className="form-group">
                <label htmlFor="resign-new-day">{t('resignManage.newDayLabel')}</label>
                <input
                  id="resign-new-day"
                  type="date"
                  min={zakres.min}
                  max={zakres.max}
                  value={dzien}
                  onChange={(e) => setDzien(e.target.value)}
                  data-cy="resign-day"
                />
              </div>
            </>
          )}

          {stan === 'trwa' && (
            <p data-cy="resign-manage-state">
              {usuniecieOd
                ? t('resignManage.stateActive', { od: odDnia, usuniecie: usuniecieOd })
                : t('resignManage.stateActiveNoDate', { od: odDnia })}
            </p>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            {stan === 'brak' && (
              <button type="button" className="btn-danger" onClick={ustaw} disabled={busy || !dzienPoprawny} data-cy="resign-enable">
                {t('resignManage.enable')}
              </button>
            )}
            {stan === 'zaplanowana' && (
              <>
                <button type="button" className="btn-secondary" onClick={zmienDzien} disabled={busy || !dzienWZakresie(dzien, zakres)} data-cy="resign-change-day">
                  {t('resignManage.changeDay')}
                </button>
                <button type="button" className="btn-secondary" onClick={odDzisZaplanowanej} disabled={busy} data-cy="resign-start-now">
                  {t('resignManage.startNow')}
                </button>
              </>
            )}
            {stan !== 'brak' && (
              <button type="button" className="btn-primary" onClick={odwolaj} disabled={busy} data-cy="resign-cancel">
                {t('resignManage.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
